import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/App.css";
import { AuthProvider, useAuth, hasAnyRole } from "@/lib/auth";
import { hasCapability, CAPABILITY } from "@/lib/capabilityPolicy";
import { usePublicBranding } from "@/lib/platformConfig";
import { Toaster } from "@/components/ui/sonner";
import ForbiddenState from "@/components/ForbiddenState";

const qc = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});
import ShellLayout from "@/layouts/ShellLayout";
import Login from "@/pages/Login";
import RoleRouter from "@/pages/RoleRouter";
import GuardianHome from "@/pages/guardian/GuardianHome";
import GuardianFees from "@/pages/guardian/GuardianFees";
import PaymentResult from "@/pages/guardian/PaymentResult";
import GuardianCirculars from "@/pages/guardian/GuardianCirculars";
import TeacherHome from "@/pages/teacher/TeacherHome";
import TeacherAttendance from "@/pages/teacher/TeacherAttendance";
import PrincipalDashboard from "@/pages/principal/Dashboard";
import Institution from "@/pages/admin/Institution";
import Academic from "@/pages/admin/Academic";
import Staff from "@/pages/admin/Staff";
import Admissions from "@/pages/admin/Admissions";
import AdmissionApplicationWizard from "@/pages/admin/AdmissionApplicationWizard";
import Students from "@/pages/admin/Students";
import StudentProfile from "@/pages/admin/StudentProfile";
import Fees from "@/pages/admin/Fees";
import Circulars from "@/pages/admin/Circulars";
import Announcements from "@/pages/admin/Announcements";
import AttendanceAdmin from "@/pages/admin/AttendanceAdmin";
import Timetable from "@/pages/admin/Timetable";
import Exams from "@/pages/admin/Exams";
import LibraryPage from "@/pages/admin/Library";
import TeacherHomework from "@/pages/teacher/TeacherHomework";
import TeacherMarks from "@/pages/teacher/TeacherMarks";
import GuardianStudy from "@/pages/guardian/GuardianStudy";
import GuardianServices from "@/pages/guardian/GuardianServices";
import GuardianGallery from "@/pages/guardian/GuardianGallery";
import Services from "@/pages/admin/Services";
import ERP from "@/pages/admin/ERP";
import Inventory from "@/pages/admin/Inventory";
import Campus from "@/pages/admin/Campus";
import Compliance from "@/pages/admin/Compliance";
import GeneralLedger from "@/pages/admin/GeneralLedger";
import Analytics from "@/pages/admin/Analytics";
import AiAssistant from "@/pages/AiAssistant";
import Nursery from "@/pages/admin/Nursery";
import Reports from "@/pages/admin/Reports";
import DocumentsSearch from "@/pages/admin/DocumentsSearch";
import PlatformSettings from "@/pages/admin/PlatformSettings";
import Kiosk from "@/pages/Kiosk";
import AccountHome from "@/pages/AccountHome";
import PasswordChange from "@/pages/PasswordChange";
import { I18nProvider } from "@/i18n";

function Protected({ children, allow, capability }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--text-secondary)]">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password && location.pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  if (allow && !hasAnyRole(user.roles, allow)) {
    return <ShellLayout><ForbiddenState /></ShellLayout>;
  }
  if (capability && !hasCapability(user, capability)) {
    return <ShellLayout><ForbiddenState /></ShellLayout>;
  }
  return <ShellLayout>{children}</ShellLayout>;
}

function BrandingTitleSync() {
  const branding = usePublicBranding();

  React.useEffect(() => {
    const appName = branding.data?.app_name || "EduOS";
    document.title = `${appName} — Education Operating System`;
  }, [branding.data?.app_name]);

  return null;
}

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={qc}>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
          <BrandingTitleSync />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/change-password" element={<PasswordChange />} />
            <Route path="/" element={<RoleRouter />} />
            <Route path="/account" element={<Protected><AccountHome /></Protected>} />

            {/* Guardian */}
            <Route path="/parent" element={<Protected allow={["guardian", "platform_admin"]} capability={[CAPABILITY.STUDENTS_READ, CAPABILITY.REPORTS_READ]}><GuardianHome /></Protected>} />
            <Route path="/parent/fees" element={<Protected allow={["guardian", "platform_admin"]} capability={CAPABILITY.FINANCE_READ}><GuardianFees /></Protected>} />
            <Route path="/parent/fees/payment" element={<Protected allow={["guardian", "platform_admin"]} capability={CAPABILITY.FINANCE_READ}><PaymentResult /></Protected>} />
            <Route path="/parent/circulars" element={<Protected allow={["guardian", "platform_admin"]} capability={[CAPABILITY.COMMUNICATION_MANAGE, CAPABILITY.REPORTS_READ]}><GuardianCirculars /></Protected>} />

            {/* Teacher */}
            <Route path="/teacher" element={<Protected allow={["teacher", "principal", "tenant_owner", "platform_admin"]} capability={CAPABILITY.ACADEMIC_READ}><TeacherHome /></Protected>} />
            <Route path="/teacher/attendance" element={<Protected allow={["teacher", "principal", "tenant_owner", "platform_admin"]} capability={CAPABILITY.ATTENDANCE_MANAGE}><TeacherAttendance /></Protected>} />

            {/* Principal / Admin */}
            <Route path="/principal" element={<Protected allow={["principal", "tenant_owner", "administrator", "platform_admin"]} capability={CAPABILITY.DASHBOARD_READ}><PrincipalDashboard /></Protected>} />
            <Route path="/institution" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={[CAPABILITY.TENANT_SETTINGS_READ, CAPABILITY.REPORTS_READ]}><Institution /></Protected>} />
            <Route path="/academic" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={CAPABILITY.ACADEMIC_MANAGE}><Academic /></Protected>} />
            <Route path="/staff" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={CAPABILITY.STAFF_MANAGE}><Staff /></Protected>} />
            <Route path="/dcuments" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={[CAPABILITY.STAFF_MANAGE, CAPABILITY.ADMISSIONS_MANAGE, CAPABILITY.DOCUMENTS_READ]}><DocumentsSearch /></Protected>} />
            <Route path="/admissions" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={[CAPABILITY.ADMISSIONS_MANAGE, CAPABILITY.REPORTS_READ]}><Admissions /></Protected>} />
            <Route path="/admissions/new" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={CAPABILITY.ADMISSIONS_MANAGE}><AdmissionApplicationWizard /></Protected>} />
            <Route path="/admissions/drafts/:applicationId" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={CAPABILITY.ADMISSIONS_MANAGE}><AdmissionApplicationWizard /></Protected>} />
            <Route path="/students" element={<Protected allow={["tenant_owner", "administrator", "principal", "accountant", "teacher", "platform_admin"]} capability={[CAPABILITY.STUDENTS_READ, CAPABILITY.REPORTS_READ, CAPABILITY.ACADEMIC_READ, CAPABILITY.FINANCE_READ]}><Students /></Protected>} />
            <Route path="/students/:sid" element={<Protected allow={["tenant_owner", "administrator", "principal", "accountant", "teacher", "platform_admin"]} capability={[CAPABILITY.STUDENTS_READ, CAPABILITY.REPORTS_READ, CAPABILITY.ACADEMIC_READ, CAPABILITY.FINANCE_READ]}><StudentProfile /></Protected>} />
            <Route path="/attendance" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={CAPABILITY.ATTENDANCE_MANAGE}><AttendanceAdmin /></Protected>} />
            <Route path="/timetable" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={CAPABILITY.ACADEMIC_MANAGE}><Timetable /></Protected>} />
            <Route path="/exams" element={<Protected allow={["tenant_owner", "principal", "platform_admin"]} capability={CAPABILITY.ACADEMIC_ASSESS}><Exams /></Protected>} />
            <Route path="/library" element={<Protected allow={["tenant_owner", "principal", "accountant", "platform_admin"]} capability={CAPABILITY.RESOURCES_READ}><LibraryPage /></Protected>} />
            <Route path="/fees" element={<Protected allow={["tenant_owner", "administrator", "principal", "accountant", "platform_admin"]} capability={CAPABILITY.FINANCE_READ}><Fees /></Protected>} />
            <Route path="/announcements" element={<Protected allow={["tenant_owner", "administrator", "principal", "teacher", "platform_admin"]} capability={[CAPABILITY.COMMUNICATION_MANAGE, CAPABILITY.REPORTS_READ]}><Announcements /></Protected>} />
            <Route path="/circulars" element={<Protected allow={["tenant_owner", "administrator", "principal", "teacher", "platform_admin"]} capability={[CAPABILITY.COMMUNICATION_MANAGE, CAPABILITY.REPORTS_READ]}><Circulars /></Protected>} />

            {/* Teacher Phase 2 */}
            <Route path="/teacher/homework" element={<Protected allow={["teacher", "principal", "tenant_owner", "platform_admin"]} capability={CAPABILITY.ACADEMIC_READ}><TeacherHomework /></Protected>} />
            <Route path="/teacher/marks" element={<Protected allow={["teacher", "principal", "tenant_owner", "platform_admin"]} capability={CAPABILITY.ACADEMIC_ASSESS}><TeacherMarks /></Protected>} />

            {/* Guardian Phase 2 */}
            <Route path="/parent/study" element={<Protected allow={["guardian", "platform_admin"]} capability={[CAPABILITY.ACADEMIC_READ, CAPABILITY.REPORTS_READ]}><GuardianStudy /></Protected>} />
            {/* Guardian Phase 3 */}
            <Route path="/parent/services" element={<Protected allow={["guardian", "platform_admin"]} capability={[CAPABILITY.TRANSPORT_READ, CAPABILITY.REPORTS_READ]}><GuardianServices /></Protected>} />
            <Route path="/parent/gallery" element={<Protected allow={["guardian", "platform_admin"]} capability={[CAPABILITY.STUDENTS_READ, CAPABILITY.REPORTS_READ]}><GuardianGallery /></Protected>} />
            {/* Admin Phase 3 */}
            <Route path="/services" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={CAPABILITY.TRANSPORT_MANAGE}><Services /></Protected>} />
            <Route path="/erp" element={<Protected allow={["tenant_owner", "principal", "accountant", "platform_admin"]} capability={CAPABILITY.RESOURCES_READ}><ERP /></Protected>} />
            <Route path="/inventory" element={<Protected allow={["tenant_owner", "administrator", "principal", "teacher", "platform_admin"]} capability={CAPABILITY.INVENTORY_ISSUE}><Inventory /></Protected>} />
            <Route path="/campus" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={[CAPABILITY.SAFETY_READ, CAPABILITY.SAFETY_MANAGE]}><Campus /></Protected>} />
            <Route path="/compliance" element={<Protected allow={["tenant_owner", "principal", "platform_admin"]} capability={[CAPABILITY.AUDIT_READ, CAPABILITY.REPORTS_READ]}><Compliance /></Protected>} />
            <Route path="/gl" element={<Protected allow={["tenant_owner", "principal", "accountant", "platform_admin"]} capability={CAPABILITY.FINANCE_READ}><GeneralLedger /></Protected>} />
            <Route path="/analytics" element={<Protected allow={["tenant_owner", "principal", "platform_admin"]} capability={CAPABILITY.REPORTS_READ}><Analytics /></Protected>} />
            <Route path="/ai" element={<Protected><AiAssistant /></Protected>} />
            <Route path="/nursery" element={<Protected allow={["tenant_owner", "administrator", "principal", "teacher", "platform_admin"]} capability={CAPABILITY.NURSERY_MANAGE}><Nursery /></Protected>} />
            <Route path="/reports" element={<Protected allow={["tenant_owner", "administrator", "principal", "accountant", "platform_admin"]} capability={CAPABILITY.REPORTS_READ}><Reports /></Protected>} />
            <Route path="/kiosk" element={<Protected allow={["tenant_owner", "administrator", "principal", "platform_admin"]} capability={CAPABILITY.KIOSK_MANAGE}><Kiosk /></Protected>} />
            <Route path="/platform/settings" element={<Protected allow={["platform_admin"]} capability={CAPABILITY.PLATFORM_CONFIG_READ}><PlatformSettings /></Protected>} />
            <Route path="/platform/branding" element={<Protected allow={["platform_admin"]} capability={CAPABILITY.PLATFORM_CONFIG_READ}><Navigate to="/platform/settings?tab=branding" replace /></Protected>} />
            <Route path="/platform/lookups" element={<Protected allow={["platform_admin"]} capability={CAPABILITY.PLATFORM_CONFIG_READ}><Navigate to="/platform/settings?tab=lookups" replace /></Protected>} />
            <Route path="/platform/configuration" element={<Protected allow={["platform_admin"]} capability={CAPABILITY.PLATFORM_CONFIG_READ}><Navigate to="/platform/settings?tab=advanced" replace /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
